import React, { useEffect, useState } from 'react';

import Progress from '../../components/ui/progress';
import { EXPERIENCE_TRANSITION_MS, REFLECTION_QUESTIONS } from '../content';
import {
  COMPLETE,
  TRANSITIONING,
  advanceExperience,
  createExperienceState,
  resetExperience,
  selectExperienceOption,
} from '../state/experienceState';
import useTransitionFocus from '../hooks/useTransitionFocus';
import NarrativeChapter from './NarrativeChapter';
import ResonanceOrb from './ResonanceOrb';

/**
 * Renders the authored five-question reflection preview. Accepts no props.
 *
 * @returns {React.ReactElement} Chapter five with one live prompt, guarded
 *   native choices, progress, and a replayable completion transition.
 * @sideEffects Schedules and clears one browser timeout after a valid choice,
 *   and restores focus after prompt replacement.
 */
export default function ReflectionPreview({ isActive, index = 3 }) {
  const [state, setState] = useState(
    () => createExperienceState(REFLECTION_QUESTIONS.length),
  );
  const promptRegionRef = useTransitionFocus(state.phase);

  useEffect(() => {
    if (!isActive) {
      setState(createExperienceState(REFLECTION_QUESTIONS.length));
    }
  }, [isActive]);


  useEffect(() => {
    if (state.phase !== TRANSITIONING) {
      return undefined;
    }

    const transitionTimer = window.setTimeout(() => {
      setState((latest) => advanceExperience(latest));
    }, EXPERIENCE_TRANSITION_MS);

    return () => window.clearTimeout(transitionTimer);
  }, [state.phase]);

  const handleSelect = (value) => {
    setState((current) => {
      const selected = selectExperienceOption(current, value);

      return selected;
    });
  };

  const isComplete = state.phase === COMPLETE;
  const question = isComplete ? null : REFLECTION_QUESTIONS[state.index];
  const progress = (state.index / state.total) * 100;
  const isTransitioning = state.phase === TRANSITIONING;

  return (
    <NarrativeChapter
      index={index}
      id="reflection"
      title="Before Milan introduces you, it asks better questions."
      body={isComplete ? null : "Reflection helps shape compatibility beyond profile photos, prompts, and quick impressions."}
    >
      <div className="experience-preview reflection-preview">
        {isComplete ? null : (
          <Progress
            value={progress}
            current={state.index}
            total={state.total}
            label="Reflection"
          />
        )}
        <div
          ref={promptRegionRef}
          className={`experience-preview__prompt experience-preview__prompt--${state.phase}`}
          aria-live="polite"
          aria-atomic="true"
          tabIndex={-1}
        >
          {isComplete ? (
            <div className="experience-preview__complete">
              <div className="experience-preview__complete-content">
                <p>There is more to compatibility than a checklist or a swipe.</p>
                <ResonanceOrb iconType="quote" />
              </div>
            </div>
          ) : (
            <>
              <p>{question.prompt}</p>
              <div className="experience-preview__options">
                {question.options.map((option) => (
                  <button
                    key={state.index + '-' + option}
                    type="button"
                    disabled={isTransitioning}
                    onClick={() => handleSelect(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </NarrativeChapter>
  );
}
